import { useFormik } from 'formik';
import { Input, InputGroup, Form, Loader, InputPicker, Button, IconButton } from 'rsuite';
import * as Yup from 'yup';

import { useEffect, useState } from 'react';

import FieldWithDebounce from "../forms/DebounceField";
import Field from "../forms/Field";

import SearchIcon from '@rsuite/icons/Search';
import CheckOutlineIcon from '@rsuite/icons/CheckOutline';
import CloseOutlineIcon from '@rsuite/icons/CloseOutline';
import TrashIcon from '@rsuite/icons/Trash';
import PlusIcon from '@rsuite/icons/Plus';

import { createBrief, get_info_by_inn, getBrief, updateBriefField, updateBriefFields } from './briefCreationManager';
import Sections from './sotrable/Sections';

export const EXPERTISE_TYPES = [
    { label: 'Экологический аудит', value: 1 },
    { label: 'ПДВ', value: 2 },
    { label: 'НМУ', value: 3 },
    { label: 'СЗЗ', value: 4 },
    { label: 'ОВОС (ГЭЭ)', value: 5 },
]

const INN_FIELDS = {
    org_name: '',
    address: '',
    OKVED: '',
    OKTMO: '',
    OGRN: '',
    KPP: '',
    FIO: '',
}

const EMPTY_CONTACT = { fio: '', email: '', phone: '' };

const validationSchema = Yup.object().shape({
    expertise_type: Yup.number().required('Тип экспертизы обязателен для заполнения'),
});

function ContactEditor({ title, contacts, onChange }) {
    const [newContact, setNewContact] = useState(EMPTY_CONTACT);
    const [adding, setAdding] = useState(false);

    const handleAdd = () => {
        if (!newContact.fio && !newContact.email && !newContact.phone) return;
        const updated = [...contacts, newContact];
        onChange(updated);
        setNewContact(EMPTY_CONTACT);
        setAdding(false);
    };

    const handleRemove = (idx) => {
        const updated = contacts.filter((_, i) => i !== idx);
        onChange(updated);
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9em', color: 'var(--rs-text-secondary)' }}>
                {title}
            </div>
            {contacts.map((c, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.3rem', fontSize: '0.85em' }}>
                    <span style={{ flex: 2 }}>{c.fio}</span>
                    <span style={{ flex: 2 }}>{c.email}</span>
                    <span style={{ flex: 2 }}>{c.phone}</span>
                    <IconButton icon={<TrashIcon />} size="xs" appearance="subtle" onClick={() => handleRemove(idx)} />
                </div>
            ))}
            {adding ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '0.4rem' }}>
                    <Input size="sm" placeholder="ФИО" value={newContact.fio} onChange={v => setNewContact(p => ({...p, fio: v}))} />
                    <Input size="sm" placeholder="E-mail" value={newContact.email} onChange={v => setNewContact(p => ({...p, email: v}))} />
                    <Input size="sm" placeholder="Телефон" value={newContact.phone} onChange={v => setNewContact(p => ({...p, phone: v}))} />
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button size="xs" appearance="primary" onClick={handleAdd}>Добавить</Button>
                        <Button size="xs" appearance="subtle" onClick={() => { setAdding(false); setNewContact(EMPTY_CONTACT); }}>Отмена</Button>
                    </div>
                </div>
            ) : (
                <Button size="xs" appearance="ghost" startIcon={<PlusIcon />} onClick={() => setAdding(true)}>
                    Добавить
                </Button>
            )}
        </div>
    );
}

export default function BriefCreateForm() {
    const [loadingINN, setLoadingINN] = useState(false);
    const [creating, setCreating] = useState(false);
    const [briefData, setBriefData] = useState([]);

    const ExpertiseTypeInput = ({...props}) => (
        <InputPicker {...props} data={EXPERTISE_TYPES} style={{width: '100%'}}/>
    );

    const InnInput = ({...props}) => (
        <InputGroup style={{width: '100%'}} inside>
            <Input {...props} style={{width: '100%'}}/>
            <InputGroup.Addon>
                {loadingINN ? <Loader speed="slow" /> :
                 formik.values.org_name ? <CheckOutlineIcon color='green' /> :
                 formik.values.INN ? <CloseOutlineIcon color='red' /> :
                 <SearchIcon />}
            </InputGroup.Addon>
        </InputGroup>
    );

    const formik = useFormik({
        initialValues: {
            name: '',
            INN: '',
            expertise_type: 1,
            contract_number: '',
            contract_date: '',
            client_contacts: [],
            eco_contacts: [],
            ...INN_FIELDS
        },
        validationSchema
    });

    useEffect(() => {
        if (localStorage.getItem('brief:current_edit')) {
            const id = localStorage.getItem('brief:current_edit');
            getBrief(id, setBriefData, formik);
        }
    }, []);

    useEffect(() => {
        if (!creating) return;
        createBrief(formik.values, setBriefData);
        setCreating(false);
    }, [creating]);

    const saveContacts = (field, value) => {
        formik.setFieldValue(field, value);
        if (localStorage.getItem('brief:current_edit')) {
            updateBriefField(localStorage.getItem('brief:current_edit'), field, value);
        }
    };

    return (
        <>
        <Form>
            <FieldWithDebounce
                handleDebouncedAction={(e) => { if (localStorage.getItem('brief:current_edit')) updateBriefField(localStorage.getItem('brief:current_edit'), 'name', e) }}
                onChange={(e) => { formik.setFieldValue('name', e); }}
            >
                <Field className="form-small-margin" as={Input} name="name" label="Название"
                    value={formik.values.name} error={formik.errors.name} />
            </FieldWithDebounce>

            <FieldWithDebounce
                handleDebouncedAction={(e) => { if (localStorage.getItem('brief:current_edit')) updateBriefField(localStorage.getItem('brief:current_edit'), 'expertise_type', e) }}
                onChange={(e) => { formik.setFieldValue('expertise_type', e); }}
            >
                <Field className="form-small-margin" as={ExpertiseTypeInput} name="expertise_type" label="Тип экспертизы"
                    value={formik.values.expertise_type} error={formik.errors.expertise_type} />
            </FieldWithDebounce>

            <FieldWithDebounce
                handleDebouncedAction={(e) => {
                    if (localStorage.getItem('brief:current_edit')) {
                        updateBriefField(localStorage.getItem('brief:current_edit'), 'INN', e);
                        get_info_by_inn(e, setLoadingINN, (data) => updateBriefFields(localStorage.getItem('brief:current_edit'), data), formik, false);
                    } else {
                        get_info_by_inn(e, setLoadingINN, setCreating, formik);
                    }
                }}
                onChange={(e) => {
                    setLoadingINN(true);
                    formik.setFieldValue('INN', e);
                    Object.keys(INN_FIELDS).forEach(key => formik.setFieldValue(key, ''));
                }}
            >
                <Field as={InnInput} name="INN" label="ИНН" placeholder="ИНН"
                    value={formik.values.INN} error={formik.errors.INN} />
            </FieldWithDebounce>

            {formik.values.org_name &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'org_name', e) }}
                onChange={(e) => { formik.setFieldValue('org_name', e); }}
            >
                <Field className="form-small-margin" as={Input} name="org_name" label="Наименование"
                    value={formik.values.org_name} error={formik.errors.org_name} />
            </FieldWithDebounce>
            }

            {formik.values.address &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'address', e) }}
                onChange={(e) => { formik.setFieldValue('address', e); }}
            >
                <Field className="form-small-margin" as={Input} name="address" label="Адрес"
                    value={formik.values.address} error={formik.errors.address} />
            </FieldWithDebounce>
            }

            {formik.values.OGRN &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'OGRN', e) }}
                onChange={(e) => { formik.setFieldValue('OGRN', e); }}
            >
                <Field className="form-small-margin" as={Input} name="OGRN" label="ОГРН"
                    value={formik.values.OGRN} error={formik.errors.OGRN} />
            </FieldWithDebounce>
            }

            {formik.values.KPP &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'KPP', e) }}
                onChange={(e) => { formik.setFieldValue('KPP', e); }}
            >
                <Field className="form-small-margin" as={Input} name="KPP" label="КПП"
                    value={formik.values.KPP} error={formik.errors.KPP} />
            </FieldWithDebounce>
            }

            {formik.values.OKVED &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'OKVED', e) }}
                onChange={(e) => { formik.setFieldValue('OKVED', e); }}
            >
                <Field className="form-small-margin" as={Input} name="OKVED" label="ОКВЭД"
                    value={formik.values.OKVED} error={formik.errors.OKVED} />
            </FieldWithDebounce>
            }

            {formik.values.OKTMO &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'OKTMO', e) }}
                onChange={(e) => { formik.setFieldValue('OKTMO', e); }}
            >
                <Field className="form-small-margin" as={Input} name="OKTMO" label="ОКТМО"
                    value={formik.values.OKTMO} error={formik.errors.OKTMO} />
            </FieldWithDebounce>
            }

            {formik.values.FIO &&
            <FieldWithDebounce
                handleDebouncedAction={(e) => { updateBriefField(localStorage.getItem('brief:current_edit'), 'FIO', e) }}
                onChange={(e) => { formik.setFieldValue('FIO', e); }}
            >
                <Field className="form-small-margin" as={Input} name="FIO" label="ФИО Руководителя"
                    value={formik.values.FIO} error={formik.errors.FIO} />
            </FieldWithDebounce>
            }

            <FieldWithDebounce
                handleDebouncedAction={(e) => { if (localStorage.getItem('brief:current_edit')) updateBriefField(localStorage.getItem('brief:current_edit'), 'contract_number', e) }}
                onChange={(e) => { formik.setFieldValue('contract_number', e); }}
            >
                <Field className="form-small-margin" as={Input} name="contract_number" label="№ договора"
                    value={formik.values.contract_number} error={formik.errors.contract_number} />
            </FieldWithDebounce>

            <FieldWithDebounce
                handleDebouncedAction={(e) => { if (localStorage.getItem('brief:current_edit')) updateBriefField(localStorage.getItem('brief:current_edit'), 'contract_date', e) }}
                onChange={(e) => { formik.setFieldValue('contract_date', e); }}
            >
                <Field className="form-small-margin" as={Input} name="contract_date" label="Дата договора"
                    value={formik.values.contract_date} error={formik.errors.contract_date} />
            </FieldWithDebounce>
        </Form>

        <div style={{ marginTop: '1rem' }}>
            <ContactEditor
                title="Ответственные лица со стороны заказчика"
                contacts={formik.values.client_contacts || []}
                onChange={(val) => saveContacts('client_contacts', val)}
            />
            <ContactEditor
                title="Ответственные лица со стороны разработчиков"
                contacts={formik.values.eco_contacts || []}
                onChange={(val) => saveContacts('eco_contacts', val)}
            />
        </div>

        {briefData.length > 0 &&
            <Sections items={briefData} setItems={setBriefData}/>
        }
        </>
    )
}
